import * as mapboxgl from "mapbox-gl/esm";

import {
  formatDistance,
  metresPerPixel,
  roundDownTo125Series,
} from "./scaleBar";

/** Widest the bar may be drawn, in pixels. */
const MAX_WIDTH_PX = 100;

/**
 * A scale bar that stays readable at the zooms the panel fits centimetre-scale
 * paths to, where mapbox's own ScaleControl reports "NaN m". See `scaleBar.ts`
 * for why its measurement fails and what this computes instead.
 *
 * Carries mapbox's own `mapboxgl-ctrl-scale` class so it looks like the
 * control it replaces; that stylesheet is already imported by MapRenderer.
 */
export class ScaleBarControl implements mapboxgl.IControl {
  #map?: mapboxgl.Map;
  #container?: HTMLElement;
  // Held as a field so the same reference registers and unregisters.
  readonly #redraw = () => this.#update();

  // What the last redraw read and what it drew. `move` fires from inside the
  // render frame for the whole of every pan, zoom and inertia glide -- and for
  // rotate and pitch, which cannot change the reading at all -- so both halves
  // are worth skipping: the camera pair avoids recomputing, the drawn pair
  // avoids touching the DOM for a bar that would look identical.
  #lastLat?: number;
  #lastZoom?: number;
  #lastLabel?: string;
  #lastWidth?: string;

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.#map = map;

    const container = document.createElement("div");
    container.className = "mapboxgl-ctrl mapboxgl-ctrl-scale";
    this.#container = container;

    // Zoom changes the resolution and panning changes the latitude, and both
    // arrive as `move`.
    map.on("move", this.#redraw);
    this.#update();

    return container;
  }

  onRemove(): void {
    this.#map?.off("move", this.#redraw);
    this.#container?.remove();
    this.#container = undefined;
    this.#map = undefined;
    // Dropped with the container they describe: a re-added control builds a
    // fresh, empty one, and a memo carried over from the last map would let
    // its first redraw skip the write that fills it.
    this.#lastLat = undefined;
    this.#lastZoom = undefined;
    this.#lastLabel = undefined;
    this.#lastWidth = undefined;
  }

  getDefaultPosition(): mapboxgl.ControlPosition {
    return "bottom-left";
  }

  #update(): void {
    const map = this.#map;
    const container = this.#container;
    if (map === undefined || container === undefined) {
      return;
    }

    const { lat } = map.getCenter();
    const zoom = map.getZoom();
    if (lat === this.#lastLat && zoom === this.#lastZoom) {
      return;
    }
    this.#lastLat = lat;
    this.#lastZoom = zoom;

    const resolution = metresPerPixel(lat, zoom);
    // Nothing readable to say for a camera that has not settled, or a latitude
    // at the pole where a pixel covers no ground. Blank rather than a stale
    // reading against the camera that is on screen.
    if (!Number.isFinite(resolution) || resolution <= 0) {
      this.#draw(container, "", "0");
      return;
    }

    const distance = roundDownTo125Series(resolution * MAX_WIDTH_PX);
    // Rounded to a tenth of a pixel: the width carries cos(latitude), so a
    // pure north-south pan perturbs it by far less than the screen can draw,
    // and the raw float would be a new string -- and a new layout -- on every
    // frame of the gesture.
    const widthPx = Math.round((distance / resolution) * 10) / 10;

    this.#draw(container, formatDistance(distance), `${widthPx}px`);
  }

  /** Writes only what would look different from the bar already on screen. */
  #draw(container: HTMLElement, label: string, width: string): void {
    if (label === this.#lastLabel && width === this.#lastWidth) {
      return;
    }
    this.#lastLabel = label;
    this.#lastWidth = width;

    container.textContent = label;
    container.style.width = width;
  }
}
